// Статистика → Чеки (Алексей 25.10, #2364: «по Ратуше чеки есть, а на вкладке статистика нет инфо по чекам»).
// Данные — та же сводка, что у панели «Чеки» в Статусе (ops_tasks/receipts/<zone>/summary/), за период
// вкладки: итоги, выручка и средний чек по дням (два графика — шкалы разные), средний чек по дню недели
// (#2280), чеки по часам, что продают, кассиры.
// 30.10 (#3413): в стиле остальной статистики — MetricCard и разделы; кассиры сортируются по столбцам;
// на одном дне «по часам» на всю ширину (раньше справа зияла пустая половина).
import React from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Link, useSearchParams } from 'react-router-dom';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Card, CardContent } from '@/components/ui/card';
import { Banknote, ChevronDown, ChevronUp, ChevronsUpDown, Clock, ReceiptText, ShoppingBasket, Undo2, UserRound, Wallet } from 'lucide-react';
import { MetricBig, MetricCard, MetricRow, MetricSmall } from '@/components/report/MetricCard';
import { getReceiptsSummary } from '@/api/receipts';
import { ReceiptsFeed } from '@/components/receipts/ReceiptsFeed';
import { cn } from '@/lib/utils';
import { isFinanceHidden, money } from '@/lib/finance';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';

const rub = (n: number | null | undefined) => (n == null ? '—' : money(`${Math.round(n).toLocaleString('ru-RU')} ₽`));
const num = (n: number) => n.toLocaleString('ru-RU');
const dayLabel = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-card)', color: 'var(--color-foreground)' };
const TICK = { fontSize: 10, fill: 'var(--color-muted-foreground)' };

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

const ACCENT = '#3b82f6';
const GRID = <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.4} />;

type OpKey = 'name' | 'count' | 'revenue' | 'avg' | 'share';
const OP_COLS: { key: OpKey; label: string; right: boolean }[] = [
  { key: 'name', label: 'Кассир', right: false },
  { key: 'count', label: 'Чеков', right: true },
  { key: 'share', label: 'Доля', right: true },
  { key: 'revenue', label: 'Выручка', right: true },
  { key: 'avg', label: 'Средний чек', right: true },
];

function SortIcon({ dir }: { dir: 'asc' | 'desc' | null }) {
  const I = dir === 'asc' ? ChevronUp : dir === 'desc' ? ChevronDown : ChevronsUpDown;
  return <I className={cn('size-3', dir ? 'text-foreground' : 'opacity-40')} />;
}

function OperatorsTable({ ops, total, operator, onOperator }: {
  ops: { name: string; count: number; revenue: number }[];
  total: number;
  operator: string;
  onOperator: (name: string) => void;
}) {
  // Клик по шапке — сортировка по столбцу (Алексей #3413); числа — сначала большие, имя — по алфавиту.
  const [sort, setSort] = React.useState<{ key: OpKey; dir: 'asc' | 'desc' }>({ key: 'count', dir: 'desc' });
  const val = (o: { name: string; count: number; revenue: number }, k: OpKey): number | string =>
    k === 'name' ? o.name || '' : k === 'count' || k === 'share' ? o.count : k === 'revenue' ? o.revenue : o.count ? o.revenue / o.count : 0;
  const rows = [...ops].sort((a, b) => {
    const x = val(a, sort.key);
    const y = val(b, sort.key);
    const c = typeof x === 'string' ? x.localeCompare(String(y), 'ru') : x - (y as number);
    return sort.dir === 'asc' ? c : -c;
  });
  const avgMax = Math.max(...ops.map((o) => (o.count ? o.revenue / o.count : 0)), 1);
  const click = (k: OpKey) =>
    setSort((s) => (s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: k === 'name' ? 'asc' : 'desc' }));
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground">
          {OP_COLS.map((c) => (
            <th key={c.key} className={cn('pb-1.5 font-normal', c.right ? 'text-right' : 'text-left')}>
              <button type="button" onClick={() => click(c.key)}
                className={cn('inline-flex items-center gap-0.5 hover:text-foreground', sort.key === c.key && 'text-foreground')}>
                {c.label}
                <SortIcon dir={sort.key === c.key ? sort.dir : null} />
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => {
          const avg = o.count ? o.revenue / o.count : null;
          return (
            <tr key={o.name} className={cn('border-t border-border/50', operator === o.name && 'bg-primary/5')}>
              <td className="py-1.5 pr-2">
                {o.name ? (
                  <UiTooltip content="Показать его чеки в ленте ниже"><button type="button" onClick={() => onOperator(o.name)}
                    className={cn('text-left underline-offset-2 hover:underline', operator === o.name && 'font-semibold text-primary')}>{o.name}</button></UiTooltip>
                ) : '—'}
              </td>
              <td className="py-1.5 text-right tabular-nums">{num(o.count)}</td>
              <td className="py-1.5 text-right tabular-nums text-muted-foreground">{total ? `${Math.round((o.count / total) * 100)} %` : '—'}</td>
              <td className="py-1.5 text-right tabular-nums">{rub(o.revenue)}</td>
              <td className="py-1.5 pl-2 text-right tabular-nums">
                <span className="inline-flex items-center justify-end gap-2">
                  <span className="hidden h-1.5 w-12 overflow-hidden rounded-full bg-muted sm:inline-block">
                    <span className="block h-full rounded-full" style={{ width: `${((avg ?? 0) / avgMax) * 100}%`, background: ACCENT }} />
                  </span>
                  {rub(avg)}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function AnalyticsReceiptsTab({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const [sp] = useSearchParams();
  const [operator, setOperator] = React.useState('');
  const { data, error, isLoading } = useSWR(
    zoneId ? ['analytics:receipts', zoneId, from, to] : null,
    () => getReceiptsSummary(zoneId, from, to).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-muted" />)}</div>
        <div className="h-56 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }
  if (!data) {
    return <Card><CardContent className="p-0"><ServerStatusNotice variant="card" error={error} /></CardContent></Card>;
  }
  if (!data.connected) {
    return (
      <Card>
        <CardContent className="flex h-32 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
          Кассы этого филиала не подключены
          <Link to={`/management/receipts?${sp.toString()}`} className="text-primary hover:underline">Подключить чеки</Link>
        </CardContent>
      </Card>
    );
  }
  if (!data.count) {
    return <Card><CardContent className="flex h-24 items-center justify-center text-sm text-muted-foreground">За выбранный период чеков нет</CardContent></Card>;
  }
  const days = data.by_day.filter((d) => d.count > 0);
  const multi = days.length > 1;
  const perDay = days.length ? data.count / days.length : 0;
  // Средний чек по дню недели — взвешенно (выручка / чеки), а не среднее средних.
  const wd = WEEKDAYS.map((name, i) => {
    const rows = days.filter((d) => (new Date(`${d.date}T00:00:00`).getDay() + 6) % 7 === i);
    const cnt = rows.reduce((s, d) => s + d.count, 0);
    const rev = rows.reduce((s, d) => s + d.revenue, 0);
    return { name, avg: cnt ? rev / cnt : null, perDay: rows.length ? cnt / rows.length : 0, days: rows.length };
  });
  const wdMax = Math.max(...wd.map((w) => w.avg ?? 0), 1);
  const showWd = days.length >= 7;
  const hours = data.by_hour.filter((h) => h.count > 0);
  const peak = hours.reduce<{ hour: number; count: number } | null>((m, h) => (!m || h.count > m.count ? h : m), null);
  const topMax = Math.max(...data.top_items.map((x) => x.qty), 1);
  const avgs = days.map((d) => d.avg_check ?? 0).filter((v) => v > 0);
  return (
    <div className="space-y-4">
      <div className="grid items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={ReceiptText} title="Чеков" hint="Продажи за период, без возвратов.">
          <MetricRow>
            <MetricBig value={num(data.count)} label="за период" className="text-foreground" />
            {multi && <MetricSmall value={num(Math.round(perDay))} label="в день" />}
          </MetricRow>
        </MetricCard>
        <MetricCard icon={Banknote} title="Выручка" hint="Сумма продаж по чекам за период.">
          <MetricRow>
            <MetricBig value={rub(data.revenue)} label="за период" className="text-foreground" />
            {multi && <MetricSmall value={rub(data.revenue / days.length)} label="в день" />}
          </MetricRow>
        </MetricCard>
        <MetricCard icon={Wallet} title="Средний чек" hint="Выручка / чеки. Ниже — разброс среднего чека по дням.">
          <MetricRow>
            <MetricBig value={rub(data.avg_check)} label="за период" style={{ color: ACCENT }} />
            {multi && avgs.length > 1 && <MetricSmall value={`${rub(Math.min(...avgs))} – ${rub(Math.max(...avgs))}`} label="по дням" />}
          </MetricRow>
        </MetricCard>
        <MetricCard icon={Undo2} title="Возвраты" hint="Чеки возврата прихода за период.">
          <MetricRow>
            <MetricBig value={num(data.returns)} label="чеков" className="text-foreground" />
            <MetricSmall value={data.count ? `${((data.returns / data.count) * 100).toFixed(1)} %` : '—'} label="от продаж" />
          </MetricRow>
        </MetricCard>
      </div>

      <SectionHeader>Динамика</SectionHeader>
      {multi && (
        <div className="grid items-stretch gap-3 lg:grid-cols-2">
          <MetricCard icon={Banknote} title="Выручка по дням">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart accessibilityLayer={false} data={days} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                {GRID}
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={TICK} axisLine={false} tickLine={false} minTickGap={20} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => (isFinanceHidden() ? '' : `${Math.round(v / 1000)}k`)} />
                <Tooltip isAnimationActive={false} cursor={{ fill: 'var(--color-muted)', opacity: 0.5 }} contentStyle={TOOLTIP_STYLE} labelFormatter={(d) => dayLabel(String(d))} formatter={(v) => [rub(Number(v)), 'Выручка']} />
                <Bar dataKey="revenue" fill={ACCENT} fillOpacity={0.8} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </MetricCard>
          <MetricCard icon={Wallet} title="Средний чек по дням">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart accessibilityLayer={false} data={days} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                {GRID}
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={TICK} axisLine={false} tickLine={false} minTickGap={20} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} domain={['auto', 'auto']} tickFormatter={(v) => (isFinanceHidden() ? '' : `${Math.round(v)}`)} />
                <Tooltip isAnimationActive={false} contentStyle={TOOLTIP_STYLE} labelFormatter={(d) => dayLabel(String(d))} formatter={(v) => [rub(Number(v)), 'Средний чек']} />
                <Area type="monotone" dataKey="avg_check" stroke={ACCENT} strokeWidth={2} fill={ACCENT} fillOpacity={0.15} baseValue="dataMin" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </MetricCard>
        </div>
      )}
      <div className={cn('grid items-stretch gap-3', showWd && hours.length > 0 && 'lg:grid-cols-2')}>
        {showWd && (
          <MetricCard icon={Wallet} title="Средний чек по дню недели" hint="Выручка / чеки по всем таким дням периода.">
            <div className="space-y-1.5 pt-1">
              {wd.map((w) => (
                <div key={w.name} className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{w.name}</span>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full" style={{ width: `${((w.avg ?? 0) / wdMax) * 100}%`, background: ACCENT }} />
                  </div>
                  <span className="w-40 text-right tabular-nums">
                    {rub(w.avg)} <span className="text-muted-foreground">· {num(Math.round(w.perDay))} чеков в день</span>
                  </span>
                </div>
              ))}
            </div>
          </MetricCard>
        )}
        {hours.length > 0 && (
          <MetricCard icon={Clock} title={multi ? 'Чеки по часам, за весь период' : 'Чеки по часам'}
            right={peak ? <span className="text-xs text-muted-foreground">пик <span className="font-semibold tabular-nums text-foreground">{peak.hour}:00–{peak.hour + 1}:00</span></span> : undefined}>
            <ResponsiveContainer width="100%" height={showWd ? 180 : 220}>
              <BarChart accessibilityLayer={false} data={hours} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                {GRID}
                <XAxis dataKey="hour" tick={TICK} axisLine={false} tickLine={false} tickFormatter={(h) => `${h}:00`} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={40} />
                <Tooltip isAnimationActive={false} cursor={{ fill: 'var(--color-muted)', opacity: 0.5 }} contentStyle={TOOLTIP_STYLE} labelFormatter={(h) => `${h}:00–${Number(h) + 1}:00`} formatter={(v) => [num(Number(v)), 'Чеков']} />
                <Bar dataKey="count" fill={ACCENT} fillOpacity={0.8} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </MetricCard>
        )}
      </div>

      <SectionHeader>Товары и кассиры</SectionHeader>
      <div className={cn('grid items-stretch gap-3', data.top_items.length > 0 && data.operators.length > 0 && 'lg:grid-cols-2')}>
        {data.top_items.length > 0 && (
          <MetricCard icon={ShoppingBasket} title="Что продают — топ-12 по выручке">
            <div className="space-y-1.5">
              {data.top_items.slice(0, 12).map((it) => (
                <div key={it.name} className="grid grid-cols-[minmax(0,1fr)_6rem_auto] items-center gap-2 text-xs">
                  <UiTooltip content={it.name}><span className="truncate">{it.name}</span></UiTooltip>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${(it.qty / topMax) * 100}%`, background: ACCENT }} /></div>
                  <span className="w-28 text-right tabular-nums">{num(Math.round(it.qty))} шт · <span className="text-muted-foreground">{rub(it.revenue)}</span></span>
                </div>
              ))}
            </div>
          </MetricCard>
        )}
        {data.operators.length > 0 && (
          <MetricCard icon={UserRound} title="Кассиры" hint="Нажми на заголовок столбца — отсортировать; на имя — его чеки в ленте ниже.">
            <OperatorsTable ops={data.operators} total={data.count} operator={operator} onOperator={setOperator} />
          </MetricCard>
        )}
      </div>

      <SectionHeader>Лента чеков</SectionHeader>
      <ReceiptsFeed zoneId={zoneId} from={from} to={to} operator={operator} onOperator={setOperator} />
    </div>
  );
}
